// Browser TTS helper. Voices are loaded asynchronously by the browser on first
// `speechSynthesis` access — we wait once and cache the chosen English voice.
//
// Browser autoplay policy: TTS only works AFTER a user gesture. The first
// successful `speak()` (typically post-login) "unlocks" subsequent calls.

let voicesPromise = null;
function loadVoices() {
  if (!("speechSynthesis" in window)) return Promise.resolve([]);
  if (voicesPromise) return voicesPromise;
  voicesPromise = new Promise((resolve) => {
    const existing = window.speechSynthesis.getVoices();
    if (existing && existing.length) {
      resolve(existing);
      return;
    }
    const handler = () => {
      const list = window.speechSynthesis.getVoices() || [];
      window.speechSynthesis.removeEventListener("voiceschanged", handler);
      resolve(list);
    };
    window.speechSynthesis.addEventListener("voiceschanged", handler);
    // Some browsers never fire voiceschanged — poll once at 1s as a fallback,
    // and finally resolve [] at 2s so callers don't hang in headless/no-voice
    // environments.
    setTimeout(() => {
      const list = window.speechSynthesis.getVoices() || [];
      if (list.length) resolve(list);
    }, 1000);
    setTimeout(() => resolve(window.speechSynthesis.getVoices() || []), 2000);
  });
  return voicesPromise;
}

function pickEnglishVoice(voices) {
  if (!voices || !voices.length) return null;
  return (
    voices.find((v) => /^en[-_]?(IN|GB|US)/i.test(v.lang)) ||
    voices.find((v) => /^en/i.test(v.lang)) ||
    voices[0]
  );
}

export async function speak(text, opts = {}) {
  if (!("speechSynthesis" in window) || !text) return false;
  try {
    const voices = await loadVoices();
    const voice = pickEnglishVoice(voices);
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = opts.rate ?? 1.0;
    u.pitch = opts.pitch ?? 1.0;
    u.volume = opts.volume ?? 1.0;
    if (voice) u.voice = voice;
    window.speechSynthesis.speak(u);
    return true;
  } catch {
    return false;
  }
}

export function chime(frequency = 920, durationMs = 300, volume = 0.12) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = frequency;
    g.gain.setValueAtTime(volume, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durationMs / 1000);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + durationMs / 1000);
  } catch {
    /* ignore */
  }
}

// Distinct urgent alarm for COMPLAINT alerts — 5 alternating square-wave pulses
// that sound clearly different from the regular assignment chime/ding.
export function urgentAlarm() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const start = ctx.currentTime;
    const pulses = [
      { freq: 1100, offset: 0.00, dur: 0.18 },
      { freq: 620,  offset: 0.22, dur: 0.18 },
      { freq: 1100, offset: 0.44, dur: 0.18 },
      { freq: 620,  offset: 0.66, dur: 0.18 },
      { freq: 1100, offset: 0.88, dur: 0.26 },
    ];
    pulses.forEach((p) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "square";
      o.frequency.value = p.freq;
      const t0 = start + p.offset;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.28, t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + p.dur);
      o.connect(g).connect(ctx.destination);
      o.start(t0);
      o.stop(t0 + p.dur + 0.02);
    });
  } catch {
    /* ignore */
  }
}

export function timeOfDayGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "good morning";
  if (h < 17) return "good afternoon";
  return "good evening";
}
